import apiClient from '../client';
import { buildJsonApiQuery } from '@/utils/jsonapi';

/**
 * Partition Values API Module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */

/**
 * Fetch partition values for a partition type
 * @param {string} partitionTypeId - Partition type UUID
 * @param {Object} [queryOptions] - JSON:API query options
 * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { value: { icontains: 'am' } })
 * @param {Array<string>|string} [queryOptions.sort] - Sort fields (e.g., ['sort_order'])
 * @param {Array<string>|string} [queryOptions.include] - Relations to include (e.g., ['partition_type'])
 * @param {number} [queryOptions.page] - Page number
 * @param {number} [queryOptions.pageSize] - Items per page
 * @returns {Promise<Object>} JSON:API response with partition values
 */
export async function fetchPartitionValues(partitionTypeId, queryOptions = {}) {
  const params = buildJsonApiQuery(queryOptions);
  const response = await apiClient.get(`partition-types/${partitionTypeId}/values/`, { params });
  return response.data;
}

/**
 * Create a partition value
 * @param {string} partitionTypeId - Partition type UUID
 * @param {Object} data - Value data {value, display_name, sort_order}
 * @returns {Promise<Object>} JSON:API response with created partition value
 */
export async function createPartitionValue(partitionTypeId, data) {
  // Extract partition_type from data if present (it should go in relationships, not attributes)
  const { partition_type, ...attributes } = data;

  const payload = {
    data: {
      type: 'partition_value',
      attributes,
      relationships: {
        partition_type: {
          data: {
            type: 'partition_type',
            id: partition_type || partitionTypeId
          }
        }
      }
    }
  };
  const response = await apiClient.post(`partition-types/${partitionTypeId}/values/`, payload);
  return response.data;
}

/**
 * Update a partition value
 * @param {string} partitionTypeId - Partition type UUID
 * @param {string} id - Partition value UUID
 * @param {Object} data - Updated data {value?, display_name?, is_active?, sort_order?}
 * @returns {Promise<Object>} JSON:API response with updated partition value
 */
export async function updatePartitionValue(partitionTypeId, id, data) {
  // Extract partition_type from data if present (it should go in relationships, not attributes)
  const { partition_type, ...attributes } = data;

  const payload = {
    data: {
      type: 'partition_value',
      id,
      attributes
    }
  };

  // Only include relationships if partition_type is being updated
  if (partition_type) {
    payload.data.relationships = {
      partition_type: {
        data: {
          type: 'partition_type',
          id: partition_type
        }
      }
    };
  }

  const response = await apiClient.patch(
    `partition-types/${partitionTypeId}/values/${id}/`,
    payload
  );
  return response.data;
}

/**
 * Delete a partition value
 * @param {string} partitionTypeId - Partition type UUID
 * @param {string} id - Partition value UUID
 * @returns {Promise<void>}
 */
export async function deletePartitionValue(partitionTypeId, id) {
  await apiClient.delete(`partition-types/${partitionTypeId}/values/${id}/`);
}

/**
 * Bulk create partition values
 * @param {string} partitionTypeId - Partition type UUID
 * @param {Array<Object>} values - Array of partition value objects {value, display_name, sort_order}
 * @returns {Promise<Object>} JSON:API response with created partition values
 */
export async function bulkCreatePartitionValues(partitionTypeId, values) {
  // Backend expects array directly, not wrapped in an object
  const response = await apiClient.post(
    `partition-types/${partitionTypeId}/values/bulk-create/`,
    values
  );
  return response.data;
}

/**
 * Reorder partition values
 * @param {string} partitionTypeId - Partition type UUID
 * @param {Array<string>} orderedIds - Array of partition value UUIDs in desired order
 * @returns {Promise<Object>} JSON:API response with updated partition values
 */
export async function reorderPartitionValues(partitionTypeId, orderedIds) {
  // Backend expects array of IDs directly, not wrapped in an object
  const response = await apiClient.patch(
    `partition-types/${partitionTypeId}/values/reorder/`,
    orderedIds
  );
  return response.data;
}
